import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SEO from '../components/SEO';
import ShareBar from '../components/ShareBar';
import Disclaimer from '../components/Disclaimer';
import { getSavingsByAge, ALL_AGES } from '../data/savingsByAge';

const fmt = n => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
// const fmtPct = n => `${n}x`; // unused

export default function SavingsByAge() {
  const { age } = useParams();
  const data = getSavingsByAge(age);

  if (!data) {
    return (
      <div style={s.page}>
        <div style={s.inner}>
          <h1 style={s.h1}>Age not found</h1>
          <p style={s.body}>We have savings data for ages 16–75.</p>
          <Link to="/savings-by-age/30" style={s.ctaBtn}>View age 30 benchmark →</Link>
        </div>
      </div>
    );
  }

  const salaryTarget = data.recommendedSavings;
  const isOnTrack = data.medianSavings >= salaryTarget * 0.7;

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `How much should a ${data.age}-year-old have saved?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `By age ${data.age}, financial experts recommend having ${data.salaryMultiple > 0 ? `${data.salaryMultiple}× your annual salary` : 'a growing emergency fund and initial retirement contributions'} saved. At a median US income of $78,000, that's ${fmt(salaryTarget)}. The median American at age ${data.age} has about ${fmt(data.medianSavings)} saved.`,
        },
      },
      {
        '@type': 'Question',
        name: `What should I do if I'm behind on savings at ${data.age}?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `If you're behind at ${data.age}, focus on: (1) maximizing your employer 401(k) match, (2) building 3–6 months of emergency savings, (3) automating contributions so you save before spending. Every extra 1% you save now compounds significantly over time.`,
        },
      },
      {
        '@type': 'Question',
        name: `What is the emergency fund target for a ${data.age}-year-old?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `For a ${data.age}-year-old, a solid emergency fund target is ${fmt(data.emergencyFundTarget)}, representing roughly 3–6 months of typical expenses. This protects you from unexpected job loss or large expenses without derailing retirement savings.`,
        },
      },
    ],
  };

  // Adjacent age pages for related links
  const ageNum = parseInt(age, 10);
  const nearbyAges = ALL_AGES.filter(a => Math.abs(a - ageNum) <= 3 && a !== ageNum).slice(0, 4);

  return (
    <div style={s.page}>
      <SEO
        title={`How Much Should a ${data.age}-Year-Old Have Saved? | KnowFi`}
        description={`Savings benchmarks for age ${data.age}: the recommended target is ${fmt(salaryTarget)} (${data.salaryMultiple > 0 ? `${data.salaryMultiple}× salary` : 'building savings'}). See how you compare to the median and what to do next.`}
        path={`/savings-by-age/${data.age}`}
        keywords={`how much should a ${data.age} year old have saved, savings by age ${data.age}, retirement savings at ${data.age}, savings benchmark ${data.age}, how much to save at ${data.age}, ${data.age} year old savings goal, net worth at ${data.age}`}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div style={s.glow} />
      <div style={s.inner}>

        {/* Breadcrumb */}
        <div style={s.breadcrumb}>
          <Link to="/" style={s.breadLink}>Home</Link>
          <span style={s.breadSep}>›</span>
          <Link to="/blog?tab=data" style={s.breadLink}>Data & Tools</Link>
          <span style={s.breadSep}>›</span>
          <span style={s.breadCurrent}>Age {data.age}</span>
        </div>

        {/* Header */}
        <div style={s.header}>
          <div style={s.ageBadge}>Age {data.age}</div>
          <h1 style={s.h1}>How Much Should a {data.age}-Year-Old Have Saved?</h1>
          <p style={s.subtitle}>Benchmarks, targets, and what to do next — all based on real data.</p>
        </div>

        {/* Share */}
        <div style={{ margin: '0 0 24px' }}>
          <ShareBar
            text={`How much should a ${data.age}-year-old have saved? Target: ${fmt(salaryTarget)} — see the full breakdown | KnowFi`}
            url={`https://knowfi.io/savings-by-age/${data.age}`}
          />
        </div>

        {/* Hero stats */}
        <div style={s.heroGrid}>
          <div style={s.heroCard}>
            <div style={s.heroLabel}>Recommended Target</div>
            <div style={{ ...s.heroStat, color: '#38bdf8' }}>{fmt(salaryTarget)}</div>
            <div style={s.heroSub}>{data.salaryMultiple > 0 ? `${data.salaryMultiple}× annual salary` : 'Start building now'}</div>
          </div>
          <div style={s.heroCard}>
            <div style={s.heroLabel}>Median American at {data.age}</div>
            <div style={{ ...s.heroStat, color: isOnTrack ? '#22c55e' : '#f59e0b' }}>{fmt(data.medianSavings)}</div>
            <div style={s.heroSub}>{isOnTrack ? '✅ Near target' : '⚠️ Below target'}</div>
          </div>
          <div style={s.heroCard}>
            <div style={s.heroLabel}>Emergency Fund Target</div>
            <div style={{ ...s.heroStat, color: '#a78bfa' }}>{fmt(data.emergencyFundTarget)}</div>
            <div style={s.heroSub}>3–6 months of expenses</div>
          </div>
        </div>

        {/* Guideline box */}
        <div style={s.guidelineBox}>
          <div style={s.guidelineLabel}>📊 Fidelity Retirement Guideline</div>
          <p style={s.guidelineText}>{data.retirementGuideline}</p>
        </div>

        {/* Context paragraph */}
        <div style={s.contextBox}>
          <p style={s.body}>{data.context}</p>
        </div>

        {/* Savings breakdown table */}
        <div style={s.sectionTitle}>Savings Breakdown at Age {data.age}</div>
        <div style={s.table}>
          <div style={s.tableRow}>
            <div style={s.tableKey}>Recommended retirement savings</div>
            <div style={s.tableVal}>{fmt(salaryTarget)}</div>
          </div>
          <div style={s.tableRow}>
            <div style={s.tableKey}>Median actual savings (US average)</div>
            <div style={s.tableVal}>{fmt(data.medianSavings)}</div>
          </div>
          <div style={s.tableRow}>
            <div style={s.tableKey}>Savings gap (median vs. target)</div>
            <div style={{ ...s.tableVal, color: salaryTarget > data.medianSavings ? '#ef4444' : '#22c55e' }}>
              {salaryTarget > data.medianSavings ? `-${fmt(salaryTarget - data.medianSavings)}` : `+${fmt(data.medianSavings - salaryTarget)}`}
            </div>
          </div>
          <div style={s.tableRow}>
            <div style={s.tableKey}>Emergency fund target</div>
            <div style={s.tableVal}>{fmt(data.emergencyFundTarget)}</div>
          </div>
          <div style={s.tableRow}>
            <div style={s.tableKey}>Salary multiple guideline</div>
            <div style={s.tableVal}>{data.salaryMultiple > 0 ? `${data.salaryMultiple}× salary` : 'Building phase'}</div>
          </div>
          {data.nextMilestone && (
            <div style={{ ...s.tableRow, borderBottom: 'none' }}>
              <div style={s.tableKey}>Next milestone</div>
              <div style={{ ...s.tableVal, color: '#38bdf8' }}>{data.nextMilestone.label}</div>
            </div>
          )}
        </div>

        {/* Salary multiples reference table */}
        <div style={s.sectionTitle}>Fidelity Salary Multiple Benchmarks</div>
        <div style={s.table}>
          {[
            { age: 30, multiple: '1×', target: '$78,000' },
            { age: 40, multiple: '3×', target: '$234,000' },
            { age: 50, multiple: '6×', target: '$468,000' },
            { age: 60, multiple: '8×', target: '$624,000' },
            { age: 67, multiple: '10×', target: '$780,000' },
          ].map(({ age: a, multiple, target }, i) => (
            <div key={a} style={{ ...s.tableRow, ...(i === 4 ? { borderBottom: 'none' } : {}), ...(a === ageNum ? { background: 'rgba(56,189,248,0.07)', borderRadius: '6px' } : {}) }}>
              <div style={s.tableKey}>
                <Link to={`/savings-by-age/${a}`} style={s.tableLink}>Age {a}</Link>
                {a === ageNum && <span style={s.youBadge}> ← You</span>}
              </div>
              <div style={s.tableVal}>{multiple} salary ({target} at median income)</div>
            </div>
          ))}
        </div>

        {/* CTAs */}
        <div style={s.ctaSection}>
          <div style={s.ctaTitle}>📐 Ready to build your plan?</div>
          <div style={s.ctaRow}>
            <Link to="/calculators" style={s.ctaBtn}>
              🔢 Retirement Calculator
            </Link>
            <Link to="/gameplan" style={s.ctaBtnSecondary}>
              🗺️ Get Your GamePlan
            </Link>
          </div>
        </div>

        {/* Related ages */}
        {nearbyAges.length > 0 && (
          <div style={s.relatedSection}>
            <div style={s.relatedTitle}>View Nearby Ages</div>
            <div style={s.relatedGrid}>
              {nearbyAges.map(a => {
                const d = getSavingsByAge(a);
                return (
                  <Link key={a} to={`/savings-by-age/${a}`} style={s.relatedCard}>
                    <div style={s.relatedAge}>Age {a}</div>
                    <div style={s.relatedTarget}>{fmt(d.recommendedSavings)}</div>
                    <div style={s.relatedSub}>recommended</div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        <Disclaimer />
      </div>
    </div>
  );
}

const s = {
  page:  { background: '#0f172a', color: '#f8fafc', minHeight: '100vh', padding: '40px 24px 80px', position: 'relative', overflow: 'hidden' },
  glow:  { position: 'absolute', top: '-60px', left: '50%', transform: 'translateX(-50%)', width: '700px', height: '400px', background: 'radial-gradient(ellipse at center, rgba(56,189,248,0.06) 0%, transparent 65%)', pointerEvents: 'none' },
  inner: { maxWidth: '800px', margin: '0 auto', position: 'relative', zIndex: 1 },

  breadcrumb:   { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '32px' },
  breadLink:    { color: '#475569', textDecoration: 'none', fontSize: '13px' },
  breadSep:     { color: '#334155', fontSize: '13px' },
  breadCurrent: { color: '#64748b', fontSize: '13px' },

  header:   { marginBottom: '32px' },
  ageBadge: { display: 'inline-block', background: 'rgba(56,189,248,0.1)', border: '1px solid rgba(56,189,248,0.25)', color: '#38bdf8', fontSize: '12px', fontWeight: '700', padding: '4px 12px', borderRadius: '20px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '14px' },
  h1:       { fontSize: 'clamp(22px, 4vw, 38px)', fontWeight: '900', margin: '0 0 12px', letterSpacing: '-0.5px', lineHeight: 1.2, color: '#f1f5f9' },
  subtitle: { fontSize: '17px', color: '#64748b', margin: 0, lineHeight: 1.55 },

  heroGrid:  { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '28px' },
  heroCard:  { background: '#1e293b', borderRadius: '12px', padding: '20px', border: '1px solid #334155', textAlign: 'center' },
  heroLabel: { fontSize: '12px', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px', fontWeight: '600' },
  heroStat:  { fontSize: 'clamp(22px, 4vw, 32px)', fontWeight: '900', letterSpacing: '-1px', lineHeight: 1.1, marginBottom: '6px' },
  heroSub:   { fontSize: '12px', color: '#475569' },

  guidelineBox:  { background: 'rgba(167,139,250,0.06)', border: '1px solid rgba(167,139,250,0.2)', borderRadius: '12px', padding: '18px 22px', marginBottom: '20px' },
  guidelineLabel:{ fontSize: '12px', fontWeight: '700', color: '#a78bfa', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' },
  guidelineText: { fontSize: '15px', color: '#94a3b8', margin: 0, lineHeight: 1.7 },

  contextBox: { background: '#1e293b', borderRadius: '12px', padding: '18px 22px', marginBottom: '28px', border: '1px solid #334155' },
  body:       { fontSize: '15px', color: '#94a3b8', lineHeight: 1.75, margin: 0 },

  sectionTitle: { fontSize: '14px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px', marginTop: '28px' },

  table:     { background: '#1e293b', borderRadius: '12px', border: '1px solid #334155', overflow: 'hidden', marginBottom: '8px' },
  tableRow:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 18px', borderBottom: '1px solid #334155' },
  tableKey:  { fontSize: '14px', color: '#64748b', flex: 1 },
  tableVal:  { fontSize: '14px', fontWeight: '700', color: '#f1f5f9', textAlign: 'right' },
  tableLink: { color: '#64748b', textDecoration: 'none' },
  youBadge:  { color: '#38bdf8', fontWeight: '700', fontSize: '12px' },

  ctaSection:     { background: 'rgba(56,189,248,0.04)', border: '1px solid rgba(56,189,248,0.15)', borderRadius: '14px', padding: '24px', marginTop: '32px', marginBottom: '32px', textAlign: 'center' },
  ctaTitle:       { fontSize: '17px', fontWeight: '700', color: '#f1f5f9', marginBottom: '16px' },
  ctaRow:         { display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' },
  ctaBtn:         { background: '#38bdf8', color: '#0f172a', padding: '12px 24px', borderRadius: '8px', textDecoration: 'none', fontWeight: '800', fontSize: '14px' },
  ctaBtnSecondary:{ background: '#1e293b', color: '#f1f5f9', padding: '12px 24px', borderRadius: '8px', textDecoration: 'none', fontWeight: '700', fontSize: '14px', border: '1px solid #334155' },

  relatedSection: { marginBottom: '32px' },
  relatedTitle:   { fontSize: '14px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' },
  relatedGrid:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '10px' },
  relatedCard:    { background: '#1e293b', borderRadius: '10px', padding: '16px', border: '1px solid #334155', textDecoration: 'none', color: '#f8fafc', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' },
  relatedAge:     { fontSize: '13px', fontWeight: '700', color: '#64748b' },
  relatedTarget:  { fontSize: '20px', fontWeight: '900', color: '#38bdf8' },
  relatedSub:     { fontSize: '11px', color: '#475569' },
};
